"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useBinanceKline } from "@/hooks/useBinanceWebSocket";
import { computeFearGreed, computeBuyZones, type OHLCV } from "@/lib/indicators";
interface TokenSignalsProps {
  symbol: string;
  tokenName: string;
  currentPrice?: number | null;
}
function formatPrice(price: number): string {
  if (price < 0.0001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  if (price < 100) return price.toFixed(3);
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export function TokenSignals(t0) {
  const $ = _c(75);
  const {
    symbol,
    tokenName,
    currentPrice
  } = t0;
  const {
    klines,
    error
  } = useBinanceKline(symbol, "1d");
  const notAvailable = error === "not_available";
  let t1;
  bb0: {
    if (klines.length < 30) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      let t3;
      if ($[1] !== klines.length) {
        t3 = {
          fearGreed: null,
          buyZones: t2,
          ohlcvLength: klines.length
        };
        $[1] = klines.length;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t1 = t3;
      break bb0;
    }
    let ohlcv;
    let t2;
    let t3;
    if ($[3] !== klines) {
      ohlcv = klines.map(_temp);
      t2 = computeFearGreed(ohlcv);
      t3 = computeBuyZones(ohlcv);
      $[3] = klines;
      $[4] = ohlcv;
      $[5] = t2;
      $[6] = t3;
    } else {
      ohlcv = $[4];
      t2 = $[5];
      t3 = $[6];
    }
    let t4;
    if ($[7] !== ohlcv.length || $[8] !== t2 || $[9] !== t3) {
      t4 = {
        fearGreed: t2,
        buyZones: t3,
        ohlcvLength: ohlcv.length
      };
      $[7] = ohlcv.length;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t1 = t4;
  }
  const {
    fearGreed,
    buyZones,
    ohlcvLength
  } = t1;
  if (notAvailable) {
    let t2;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <h3 className="text-lg font-semibold text-white mb-2">Market Signals</h3>;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    if ($[12] !== tokenName) {
      t3 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">{t2}<p className="text-sm text-gray-500">Signals require Binance spot data — not available for {tokenName}.</p></div>;
      $[12] = tokenName;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  }
  if (!fearGreed || ohlcvLength < 30) {
    let t2;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6 animate-pulse"><div className="h-6 bg-[hsl(230,15%,14%)] rounded w-1/3 mb-4" /><div className="h-40 bg-[hsl(230,15%,10%)] rounded" /></div>;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    return t2;
  }
  const gaugeAngle = fearGreed.score / 100 * 180 - 90;
  const px = currentPrice ?? klines[klines.length - 1]?.close ?? 0;
  let t2;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h3 className="text-lg font-semibold text-white mb-1">Market Signals</h3>;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== ohlcvLength || $[17] !== tokenName) {
    t3 = <div className="p-6 border-b border-[hsl(var(--border))]">{t2}<p className="text-xs text-gray-500">Computed from {ohlcvLength} days of {tokenName} price action</p></div>;
    $[16] = ohlcvLength;
    $[17] = tokenName;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">{"Fear & Greed Index"}</h4>;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const t5 = `${fearGreed.color}22`;
  const t6 = `${fearGreed.color}55`;
  let t7;
  if ($[20] !== fearGreed.color || $[21] !== t5 || $[22] !== t6) {
    t7 = {
      backgroundColor: t5,
      color: fearGreed.color,
      borderColor: t6,
      borderWidth: 1,
      borderStyle: "solid"
    };
    $[20] = fearGreed.color;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== fearGreed.label || $[25] !== t7) {
    t8 = <div className="flex items-center justify-between mb-4">{t4}<span className="text-xs font-bold px-2.5 py-1 rounded-full" style={t7}>{fearGreed.label}</span></div>;
    $[24] = fearGreed.label;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t10;
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <defs><linearGradient id="fg-gradient" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#dc2626" /><stop offset="25%" stopColor="#f97316" /><stop offset="50%" stopColor="#eab308" /><stop offset="75%" stopColor="#84cc16" /><stop offset="100%" stopColor="#22c55e" /></linearGradient></defs>;
    t10 = <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#fg-gradient)" strokeWidth="16" strokeLinecap="round" />;
    $[27] = t10;
    $[28] = t9;
  } else {
    t10 = $[27];
    t9 = $[28];
  }
  const t11 = `rotate(${gaugeAngle} 100 100)`;
  let t12;
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <line x1="100" y1="100" x2="100" y2="28" stroke="white" strokeWidth="3" strokeLinecap="round" />;
    t13 = <circle cx="100" cy="100" r="6" fill="white" />;
    $[29] = t12;
    $[30] = t13;
  } else {
    t12 = $[29];
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t11) {
    t14 = <g transform={t11}>{t12}{t13}</g>;
    $[31] = t11;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== fearGreed.score) {
    t15 = <text x="100" y="90" textAnchor="middle" fontSize="28" fontWeight="bold" fill="white" fontFamily="monospace">{fearGreed.score}</text>;
    $[33] = fearGreed.score;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <text x="100" y="110" textAnchor="middle" fontSize="12" fill="#9ca3af">/ 100</text>;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t14 || $[37] !== t15) {
    t17 = <div className="flex items-center justify-center my-4"><svg viewBox="0 0 200 120" className="w-full max-w-xs">{t9}{t10}{t14}{t15}{t16}</svg></div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== fearGreed.components.rsi) {
    t18 = {
      label: "RSI",
      value: fearGreed.components.rsi,
      weight: "25%"
    };
    $[39] = fearGreed.components.rsi;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== fearGreed.components.momentum) {
    t19 = {
      label: "Momentum",
      value: fearGreed.components.momentum,
      weight: "20%"
    };
    $[41] = fearGreed.components.momentum;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== fearGreed.components.volatility) {
    t20 = {
      label: "Volatility",
      value: fearGreed.components.volatility,
      weight: "20%"
    };
    $[43] = fearGreed.components.volatility;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== fearGreed.components.volumeTrend) {
    t21 = {
      label: "Volume",
      value: fearGreed.components.volumeTrend,
      weight: "15%"
    };
    $[45] = fearGreed.components.volumeTrend;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== fearGreed.components.drawdown) {
    t22 = {
      label: "Drawdown",
      value: fearGreed.components.drawdown,
      weight: "20%"
    };
    $[47] = fearGreed.components.drawdown;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== t22) {
    t23 = <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-4">{[t18, t19, t20, t21, t22].map(_temp2)}</div>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t17 || $[56] !== t23 || $[57] !== t8) {
    t24 = <div className="p-6 border-b border-[hsl(var(--border))]">{t8}{t17}{t23}</div>;
    $[55] = t17;
    $[56] = t23;
    $[57] = t8;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Optimal Buy Zones</h4>;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== px) {
    t26 = formatPrice(px);
    $[60] = px;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t26) {
    t27 = <div className="flex flex-wrap items-center justify-between gap-2 mb-4">{t25}<span className="text-xs text-gray-500 font-mono">Current: ${t26}</span></div>;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== buyZones || $[65] !== px) {
    t28 = buyZones.length === 0 ? <p className="text-sm text-gray-500 py-4 text-center">No high-confidence buy zones detected. Market conditions do not currently favor a low-risk entry — wait for RSI or Bollinger Band signals.</p> : <div className="space-y-3">{buyZones.map((zone, i) => {
        const distancePct = (px - zone.priceHigh) / px * 100;
        const isInZone = px >= zone.priceLow && px <= zone.priceHigh;
        return <div key={i} className={`rounded-lg border p-3 sm:p-4 ${isInZone ? "bg-emerald-900/20 border-emerald-700/50" : "bg-[hsl(230,15%,10%)] border-[hsl(230,15%,18%)]"}`}><div className="flex flex-wrap items-start justify-between gap-2 mb-2"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className="text-xs font-semibold text-gray-400">Zone #{i + 1}</span>{isInZone && <span className="text-[11px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full whitespace-nowrap">PRICE IN ZONE</span>}</div><p className="text-base sm:text-lg font-bold text-white font-mono mt-1 break-words">${formatPrice(zone.priceLow)} – ${formatPrice(zone.priceHigh)}</p>{!isInZone && <p className="text-xs text-gray-500 mt-0.5">{distancePct > 0 ? `${distancePct.toFixed(1)}% below current price` : `${Math.abs(distancePct).toFixed(1)}% above current`}</p>}</div><div className="text-right"><p className="text-[11px] text-gray-500 uppercase tracking-wide">Confidence</p><p className="text-2xl font-bold font-mono" style={{
                color: zone.confidence >= 75 ? "#22c55e" : zone.confidence >= 60 ? "#eab308" : "#94a3b8"
              }}>{zone.confidence}</p></div></div><div className="flex flex-wrap gap-1.5 mt-2">{zone.reasons.map(_temp3)}</div></div>;
      })}</div>;
    $[64] = buyZones;
    $[65] = px;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t27 || $[68] !== t28) {
    t29 = <div className="p-4 sm:p-6">{t27}{t28}</div>;
    $[67] = t27;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="px-4 sm:px-6 py-3 border-t border-[hsl(var(--border))] bg-[hsl(230,15%,6%)] text-[11px] text-gray-500"><strong className="text-gray-400">Disclaimer:</strong> Signals are computed from public market data and are not financial advice. Always do your own research before trading.</div>;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t24 || $[72] !== t29 || $[73] !== t3) {
    t31 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] overflow-hidden">{t3}{t24}{t29}{t30}</div>;
    $[71] = t24;
    $[72] = t29;
    $[73] = t3;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  return t31;
}
function _temp3(r, ri) {
  return <span key={ri} className="text-[11px] px-2 py-0.5 rounded-full bg-[hsl(230,15%,14%)] text-gray-400 border border-[hsl(230,15%,20%)]">{r}</span>;
}
function _temp2(c) {
  return <div key={c.label} className="bg-[hsl(230,15%,10%)] rounded-lg p-2 text-center"><p className="text-[11px] text-gray-500 uppercase tracking-wide leading-tight">{c.label}</p><p className="text-sm font-bold font-mono text-white mt-0.5">{c.value}</p><div className="h-0.5 bg-[hsl(230,15%,16%)] rounded-full mt-1 overflow-hidden"><div className="h-full transition-all" style={{
        width: `${c.value}%`,
        backgroundColor: c.value <= 20 ? "#dc2626" : c.value <= 40 ? "#f97316" : c.value <= 60 ? "#eab308" : c.value <= 80 ? "#84cc16" : "#22c55e"
      }} /></div><p className="text-[11px] text-gray-600 mt-0.5">{c.weight}</p></div>;
}
function _temp(k) {
  return {
    time: k.time,
    open: k.open,
    high: k.high,
    low: k.low,
    close: k.close,
    volume: k.volume
  };
}